import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import authService, {
  User,
  LoginCredentials,
  RegistrationData,
  AuthState,
} from "../services/auth";
import api from "../services/api";

// Define the auth context type
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegistrationData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  validateToken: () => Promise<boolean>;
}

// Create the auth context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  updateUser: () => {},
  validateToken: async () => false,
});

// Custom event name for auth state changes
export const AUTH_STATE_CHANGE_EVENT = "auth_state_change";

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Auth state
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // Function to validate token with the API
  const validateToken = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return false;

      // Call the validate-token endpoint with the correct path
      const response = await api.get<{ valid: boolean }>(
        "/api/v1/auth/validate-token",
      );
      return response.data?.valid === true;
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  };

  // Initialize auth state on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is authenticated
        if (authService.isAuthenticated()) {
          const user = authService.getCurrentUser();
          console.log("Auth initialized - User authenticated:", user?.username);
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          // Try to refresh token
          console.log("Auth initialized - Attempting token refresh");
          const refreshed = await authService.refreshToken();
          if (refreshed) {
            const user = authService.getCurrentUser();
            console.log(
              "Auth initialized - Token refreshed, user:",
              user?.username,
            );
            setAuthState({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            console.log(
              "Auth initialized - Not authenticated and token refresh failed",
            );
            setAuthState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : "Failed to initialize authentication",
        });
      }
    };

    initializeAuth();
  }, []);

  // Listen for storage events to sync auth state across tabs
  useEffect(() => {
    // Handle auth state changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "authToken" || event.key === "authUser") {
        console.log(`Storage changed for ${event.key} - Updating auth state`);

        // Check if token was removed
        if (!localStorage.getItem("authToken")) {
          console.log("Token removed - Clearing auth state");
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          return;
        }

        // Check if token was added
        if (
          localStorage.getItem("authToken") &&
          localStorage.getItem("authUser")
        ) {
          try {
            const user = JSON.parse(localStorage.getItem("authUser") || "{}");
            if (user && user.id) {
              console.log("Token added - Setting auth state");
              setAuthState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
            }
          } catch (error) {
            console.error("Error parsing auth user from storage:", error);
          }
        }
      }
    };

    // Listen for auth state changes from this application
    const handleAuthStateChange = (event: CustomEvent) => {
      const { isAuthenticated, user } = event.detail;
      console.log(
        "Auth state change event received:",
        isAuthenticated ? "Authenticated" : "Not authenticated",
      );

      setAuthState((prevState) => ({
        ...prevState,
        isAuthenticated,
        user,
        isLoading: false,
      }));
    };

    // Listen for manual token clearing (e.g. from api.ts on 401)
    const handleAuthTokenCleared = () => {
      console.log("auth-token-cleared event received - Clearing auth state");
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    };

    // Add event listeners
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(
      AUTH_STATE_CHANGE_EVENT,
      handleAuthStateChange as EventListener,
    );
    window.addEventListener("auth-token-cleared", handleAuthTokenCleared);

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        AUTH_STATE_CHANGE_EVENT,
        handleAuthStateChange as EventListener,
      );
      window.removeEventListener("auth-token-cleared", handleAuthTokenCleared);
    };
  }, []);

  // Dispatch auth state change event
  const dispatchAuthStateChange = (
    isAuthenticated: boolean,
    user: User | null,
  ) => {
    const event = new CustomEvent(AUTH_STATE_CHANGE_EVENT, {
      detail: { isAuthenticated, user },
    });
    window.dispatchEvent(event);
  };

  // Login handler
  const login = async (credentials: LoginCredentials) => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const user = await authService.login(credentials);
      console.log(
        "Login successful in AuthContext - Setting authenticated state for:",
        user.username,
      );

      // Update state and dispatch event
      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      dispatchAuthStateChange(true, user);
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Login failed",
      }));
      throw error;
    }
  };

  // Register handler
  const register = async (data: RegistrationData) => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const user = await authService.register(data);

      // Update state and dispatch event
      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      dispatchAuthStateChange(true, user);
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Registration failed",
      }));
      throw error;
    }
  };

  // Logout handler
  const logout = async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));

    try {
      await authService.logout();

      // Update state and dispatch event
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      dispatchAuthStateChange(false, null);
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local auth state even if API call fails
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      dispatchAuthStateChange(false, null);
    }
  };

  // Update user handler
  const updateUser = (user: User) => {
    setAuthState((prev) => ({
      ...prev,
      user,
    }));

    // Persist updated user to localStorage
    localStorage.setItem("authUser", JSON.stringify(user));

    dispatchAuthStateChange(true, user);
  };

  // Context value
  const value = {
    ...authState,
    login,
    register,
    logout,
    updateUser,
    validateToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
